import { useEffect, useMemo, useState } from 'react'
import {
  ArrowDown,
  ArrowRight,
  BadgeCheck,
  BookmarkCheck,
  Cast,
  ChevronDown,
  Clock,
  Cloud,
  Fullscreen,
  HardDrive,
  Images,
  KeyRound,
  List,
  ListVideo,
  ListRestart,
  LockKeyhole,
  Menu,
  Palette,
  Pause,
  Play,
  RadioTower,
  RefreshCw,
  Search,
  ScanLine,
  Settings2,
  Shuffle,
  SortAsc,
  Square,
  SkipBack,
  SkipForward,
  Tv,
  UserRoundCheck,
  Volume2,
  VolumeX,
  X,
} from 'lucide-react'
import { APP_VERSION_LABEL } from '../../version'

const library = [
  { name: 'Private Premiere', meta: '277.2 MB  ·  Apr 5, 2021' },
  { name: '[Raw Film Club] Romeo Session', meta: '277.2 MB  ·  Apr 5, 2021' },
  { name: 'RFC Fuck My Slam Piece (Alejandro, Dev)', meta: '402.9 MB  ·  Aug 1, 2021', active: true },
  { name: 'After Hours Archive', meta: '684 MB  ·  Jul 12, 2021' },
  { name: 'Velvet Room', meta: '920 MB  ·  Jun 18, 2021' },
]

const steps = [
  {
    number: '01',
    title: 'Connect',
    body: 'Choose Google Drive or OneDrive, then select the cloud folder that becomes your library.',
  },
  {
    number: '02',
    title: 'Curate',
    body: 'Search, sort, queue, and save playlists that remember their order, current video, and playback position.',
  },
  {
    number: '03',
    title: 'Experience',
    body: 'Enter Theater Mode or open a clean broadcast output, then invite time-limited guests when needed.',
  },
]

const features = [
  {
    icon: Cloud,
    title: 'Google Drive + OneDrive',
    body: 'Connect either private cloud source, choose a folder, and turn its video files into a playable library.',
  },
  {
    icon: Images,
    title: 'Visual Media Library',
    body: 'Browse provider-backed thumbnails, duration, file details, compatibility guidance, and room-scoped play history.',
  },
  {
    icon: BookmarkCheck,
    title: 'Playlists That Resume',
    body: 'Saved playlists retain their queue, current video, and playback position so every viewing session can continue.',
  },
  {
    icon: Palette,
    title: 'Independent Rooms',
    body: 'Create separate rooms with their own identity, playlists, playback rules, guest access, and broadcast credentials.',
  },
  {
    icon: ScanLine,
    title: 'Scrub Previews',
    body: 'See a visual preview and timestamp while exploring the timeline, with graceful fallbacks when frames are unavailable.',
  },
  {
    icon: Shuffle,
    title: 'Fairer Shuffle',
    body: 'Every video stays eligible while less-played titles are weighted more heavily, keeping long collections fresh.',
  },
  {
    icon: Cast,
    title: 'Clean Broadcast Output',
    body: 'Open a distraction-free output window for meetings and control playback from the main player.',
  },
  {
    icon: BadgeCheck,
    title: 'Playback Diagnostics',
    body: 'Bounded, structured events make startup, buffering, failures, and recovery easier to understand without storing credentials.',
  },
]

const privacyProof = [
  {
    icon: Cloud,
    number: '01',
    title: 'Read-Only Cloud Access',
    body: 'Cloud sources are connected with file-reading permissions, never edit or delete permissions.',
  },
  {
    icon: KeyRound,
    number: '02',
    title: 'Encrypted Provider Tokens',
    body: 'Stored Google Drive and OneDrive connection tokens are encrypted at rest.',
  },
  {
    icon: UserRoundCheck,
    number: '03',
    title: 'Owner-Controlled Guests',
    body: 'Temporary guest invitations expire automatically and can be revoked early.',
  },
  {
    icon: RadioTower,
    number: '04',
    title: 'Rotatable Broadcast Links',
    body: 'Broadcast output uses a long private token that the owner can disable or rotate.',
  },
]

const roadmap = [
  {
    icon: RefreshCw,
    status: 'NEXT UP',
    title: 'Durable Sessions',
    body: 'Longer intentional owner sessions and even clearer diagnostics when playback needs attention.',
  },
  {
    icon: SkipForward,
    status: 'NEXT UP',
    title: 'Seamless Handoffs',
    body: 'Faster transitions with a deliberate “Next up” state whenever the following stream needs a moment.',
  },
  {
    icon: Palette,
    status: 'NEXT UP',
    title: 'Premium Room Identity',
    body: 'A richer room mark, stronger now-playing hierarchy, and branded surfaces designed for viewing distance.',
  },
  {
    icon: Images,
    status: 'NEXT UP',
    title: 'Library + Video Detail',
    body: 'A dedicated visual library and video-detail view for metadata, compatibility, and playback history.',
  },
  {
    icon: ListRestart,
    status: 'NEXT UP',
    title: 'Playlist Refresh',
    body: 'Stronger artwork, hierarchy, active states, and queue affordances for a clearer media-browsing experience.',
  },
]

const faq = [
  {
    q: 'Which cloud sources are supported?',
    a: 'CLOUD PLAYER supports Google Drive and OneDrive. Connect one provider, choose a folder, and the app builds a searchable playlist from its video files.',
  },
  {
    q: 'How do queues and saved playlists work?',
    a: 'Add any video to the queue, reorder it, or play it immediately. Named playlists preserve their queue, current item, and playback position so you can resume or restart later.',
  },
  {
    q: 'What can I customize?',
    a: 'The current room profile supports a custom app name, accent color, welcome message, and a recurring bumper selected from the active library.',
  },
  {
    q: 'What does Broadcast Output do?',
    a: 'It opens a clean playback-only view for meetings or screen sharing. The main app can send video, play/pause, seek, and volume commands to a local output window.',
  },
  {
    q: 'How does compatibility guidance work?',
    a: 'CLOUD PLAYER checks the reported file type and your browser’s advertised support, then labels media as playable, worth checking, or risky before playback.',
  },
]

function BrandMark() {
  return (
    <span className="lux-brand" aria-label="Cloud Player">
      <span className="lux-brand__mark"><Play size={11} fill="currentColor" /></span>
      <span>CLOUD PLAYER</span>
    </span>
  )
}

function PlayerPreview() {
  const [playing, setPlaying] = useState(false)
  const [query, setQuery] = useState('')
  const [sidebarOpen, setSidebarOpen] = useState(true)
  const [sortMode, setSortMode] = useState('shuffle')
  const [selectionMode, setSelectionMode] = useState(false)

  const visibleItems = useMemo(
    () => library.filter((item) => item.name.toLowerCase().includes(query.toLowerCase())),
    [query],
  )

  return (
    <div className={`lux-player real-player ${sidebarOpen ? '' : 'real-player--wide'}`}>
      <header className="real-player__header">
        <div className="real-player__brand">
          <span><Play size={9} fill="currentColor" /></span>
          <strong>THE VERY IMPORTANT PLAYER</strong>
          <i />
          <small><b /> Methflixxx+</small>
        </div>
        <div className="real-player__header-actions">
          <button type="button"><Cast size={13} /><span>Broadcast</span></button>
          <button type="button" aria-label="Theater mode"><Tv size={14} /></button>
          <button type="button" aria-label="Settings"><Settings2 size={14} /></button>
        </div>
      </header>

      <div className="real-player__body">
        <div className="real-player__stage">
          <img className="real-player__video-image" src="/media/coastal-journey.png" alt="Moonlit coastline playing in Cloud Player" />
          <div className="real-player__title">
            <strong>RFC Fuck My Slam Piece (Alejandro, Dev)</strong>
            <span>3 of 133</span>
          </div>
          <span className="real-player__watermark">PPV</span>

          <div className="real-player__controls">
            <button type="button" aria-label="Previous"><SkipBack size={13} fill="currentColor" /></button>
            <button className="real-player__play" type="button" onClick={() => setPlaying(!playing)} aria-label={playing ? 'Pause' : 'Play'}>
              {playing ? <Pause size={18} fill="currentColor" /> : <Play size={18} fill="currentColor" />}
            </button>
            <button className="real-player__next" type="button" aria-label="Next"><SkipForward size={14} fill="currentColor" /></button>
            <span>0:07</span>
            <div className="real-player__progress"><i className={playing ? 'is-playing' : ''} /></div>
            <span>22:10</span>
            <button type="button" aria-label="Mute"><VolumeX size={14} /></button>
            <div className="real-player__volume"><i /></div>
            <span className="real-player__percent">0%</span>
            <button type="button" onClick={() => setSidebarOpen(!sidebarOpen)} aria-label="Toggle playlist"><ListVideo size={14} /></button>
            <button type="button" aria-label="Fullscreen"><Fullscreen size={14} /></button>
          </div>
        </div>

        {sidebarOpen && (
          <aside className="real-player__sidebar">
            <div className="real-player__playlist-head">
              <div><strong>Playlist</strong><span>133 videos · 68.9 GB</span></div>
              <button type="button" aria-label="Refresh playlist"><RefreshCw size={13} /></button>
            </div>
            <label className="real-player__search">
              <Search size={12} />
              <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search videos…" />
            </label>
            <div className="real-player__sorts">
              {[
                ['name', <SortAsc size={10} />, 'Name'],
                ['date', <Clock size={10} />, 'Date'],
                ['size', <HardDrive size={10} />, 'Size'],
                ['shuffle', <Shuffle size={10} />, 'Shuffle'],
              ].map(([value, icon, label]) => (
                <button key={String(value)} type="button" className={sortMode === value ? 'is-active' : ''} onClick={() => setSortMode(String(value))}>{icon}{label}</button>
              ))}
            </div>
            <button className={`real-player__select ${selectionMode ? 'is-active' : ''}`} type="button" onClick={() => setSelectionMode(!selectionMode)}><Square size={11} /> {selectionMode ? 'Done' : 'Select'}</button>

            <section className="real-player__saved">
              <div><strong><List size={12} /> Saved playlists</strong><button type="button">Reload</button></div>
            </section>

            <div className="real-player__list">
              {visibleItems.map((item, index) => (
                <button key={item.name} type="button" className={item.active ? 'is-active' : ''}>
                  <span>{item.active ? <Volume2 size={11} /> : index + 1}</span>
                  <span><strong>{item.name}</strong><small>{item.meta}</small><i>PLAYABLE</i></span>
                </button>
              ))}
              {visibleItems.length === 0 && <p>No matching media</p>}
            </div>
          </aside>
        )}
      </div>
    </div>
  )
}

export function LandingPage() {
  const [menuOpen, setMenuOpen] = useState(false)

  useEffect(() => {
    document.body.classList.add('landing-page')
    const nodes = Array.from(document.querySelectorAll<HTMLElement>('[data-reveal]'))
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) entry.target.classList.add('is-visible')
      })
    }, { threshold: 0.1 })
    nodes.forEach((node) => observer.observe(node))
    return () => {
      observer.disconnect()
      document.body.classList.remove('landing-page')
    }
  }, [])

  return (
    <div className="landing-shell lux-shell">
      <header className="landing-nav lux-nav">
        <a href="#top" className="landing-nav__logo"><BrandMark /></a>
        <nav className={menuOpen ? 'is-open' : ''} aria-label="Main navigation">
          <a href="#process" onClick={() => setMenuOpen(false)}>The Process</a>
          <a href="#features" onClick={() => setMenuOpen(false)}>Features</a>
          <a href="#roadmap" onClick={() => setMenuOpen(false)}>Roadmap</a>
          <a href="#privacy" onClick={() => setMenuOpen(false)}>Privacy</a>
          <a href="#faq" onClick={() => setMenuOpen(false)}>Support</a>
        </nav>
        <a className="lux-button lux-button--small" href="/app">ENTER CLOUD PLAYER <ArrowRight size={14} /></a>
        <button
          className="landing-nav__menu"
          type="button"
          aria-label="Toggle navigation"
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen(!menuOpen)}
        >
          {menuOpen ? <X /> : <Menu />}
        </button>
      </header>

      <main id="top">
        <section className="lux-hero landing-container">
          <div className="lux-hero__copy" data-reveal>
            <a className="lux-badge" href="#features"><span /> {APP_VERSION_LABEL} LIVE <i /> EXPLORE WHAT'S NEW <ArrowRight size={12} /></a>
            <h1>Your cloud library,<br />made to <em>watch.</em></h1>
            <p>Turn Google Drive or OneDrive folders into private viewing rooms with a visual library, resumable playlists, fairer shuffle, Theater Mode, and clean broadcast output.</p>
            <div className="lux-hero__actions">
              <a className="lux-button" href="#features">EXPLORE THE PLATFORM <ArrowDown size={15} /></a>
            </div>
            <div className="lux-trust"><LockKeyhole size={14} /> Read-only cloud access. Encrypted provider tokens.</div>
          </div>
          <div className="lux-hero__visual" data-reveal>
            <PlayerPreview />
            <div className="lux-visual-meta"><span>PRIVATE BROADCAST</span><span>GOOGLE DRIVE · ONEDRIVE</span></div>
          </div>
        </section>

        <div className="lux-signal" aria-hidden="true"><span /><i /><span /></div>

        <section className="lux-process landing-container" id="process">
          <div className="lux-section-heading" data-reveal>
            <span className="lux-eyebrow">THE PROCESS</span>
            <h2>From cloud folder to curated<br />viewing in three steps.</h2>
          </div>
          <div className="lux-process__grid">
            {steps.map((step) => (
              <article key={step.number} data-reveal>
                <span className="lux-process__number">{step.number}</span>
                <div className="lux-process__line"><i /></div>
                <h3>{step.title}</h3>
                <p>{step.body}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="lux-features" id="features">
          <div className="landing-container lux-features__inner">
            <div className="lux-features__copy" data-reveal>
              <span className="lux-eyebrow">NOW IN CLOUD PLAYER</span>
              <h2>More control over every private viewing session.</h2>
              <p>Cloud Player now combines visual browsing, durable playback state, independent rooms, safer sharing, and a clean meeting output around the library you already keep in the cloud.</p>
              <div className="lux-features__rule"><span>08</span><i /><span>SHIPPED FOUNDATIONS</span></div>
            </div>
            <div className="lux-feature-grid">
              {features.map((feature, index) => (
                <article key={feature.title} data-reveal>
                  <div className="lux-feature-icon"><feature.icon size={20} strokeWidth={1.25} /></div>
                  <span>{String(index + 1).padStart(2, '0')}</span>
                  <h3>{feature.title}</h3>
                  <p>{feature.body}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="lux-roadmap" id="roadmap">
          <div className="landing-container">
            <div className="lux-roadmap__heading" data-reveal>
              <div>
                <span className="lux-eyebrow">WHAT WE'RE IMPROVING NEXT</span>
                <h2>The next layer of<br /><em>the experience.</em></h2>
              </div>
              <p>The foundations are in place. The active roadmap now focuses on session reliability, seamless handoffs, richer room identity, and a more visual way to browse.</p>
            </div>
            <div className="lux-roadmap__grid">
              {roadmap.map((item, index) => (
                <article key={item.title} data-reveal>
                  <div className="lux-roadmap__top">
                    <div className="lux-roadmap__icon"><item.icon size={18} strokeWidth={1.25} /></div>
                    <span className={`lux-status lux-status--${item.status.toLowerCase().replace(/\s+/g, '-')}`}>{item.status}</span>
                  </div>
                  <span className="lux-roadmap__number">{String(index + 1).padStart(2, '0')}</span>
                  <h3>{item.title}</h3>
                  <p>{item.body}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="lux-visual-break">
          <img src="/media/coastal-journey.png" alt="Moonlit ocean landscape" />
          <div className="lux-visual-break__veil" />
          <div className="landing-container" data-reveal>
            <span className="lux-eyebrow">THEATER MODE</span>
            <h2>Less interface.<br />More <em>focus.</em></h2>
            <p>One action hides the app chrome and playlist for a video-first view. Press T to enter Theater Mode, or Escape whenever you want the controls back.</p>
          </div>
          <div className="lux-visual-break__frame"><span>PLAYING</span><i /><span>01:18:42</span></div>
        </section>

        <section className="lux-privacy landing-container" id="privacy">
          <div className="lux-privacy__copy" data-reveal>
            <span className="lux-eyebrow">CONTROLLED ACCESS</span>
            <h2>Your library stays<br />under your <em>control.</em></h2>
            <p>CLOUD PLAYER connects to your existing Google Drive or OneDrive library with read-only file access. Provider tokens are encrypted at rest, while owner controls manage temporary guests and private broadcast links.</p>
            <a className="lux-text-link" href="#faq">Review our Privacy Protocol <ArrowRight size={14} /></a>
          </div>
          <div className="lux-privacy__proof" data-reveal>
            {privacyProof.map((item) => (
              <article key={item.number}>
                <div className="lux-privacy__icon"><item.icon size={20} strokeWidth={1.15} /></div>
                <div><h3>{item.title}</h3><p>{item.body}</p></div>
                <span>{item.number}</span>
              </article>
            ))}
          </div>
        </section>

        <section className="lux-faq landing-container" id="faq">
          <div className="lux-section-heading" data-reveal>
            <span className="lux-eyebrow">SUPPORT</span>
            <h2>Discreet questions.<br />Clear answers.</h2>
          </div>
          <div className="lux-faq__list" data-reveal>
            {faq.map((item, index) => (
              <details key={item.q}>
                <summary><span>{String(index + 1).padStart(2, '0')}</span>{item.q}<ChevronDown size={17} /></summary>
                <p>{item.a}</p>
              </details>
            ))}
          </div>
        </section>

        <section className="lux-final-cta">
          <div className="landing-container" data-reveal>
            <span className="lux-eyebrow">GET STARTED</span>
            <h2>Curate your library.<br /><em>Control the room.</em></h2>
            <p>Connect your cloud folder, build the queue, personalize the player, and open a clean output when it is time to watch.</p>
            <a className="lux-button lux-button--light" href="/app">OPEN CLOUD PLAYER <ArrowRight size={15} /></a>
          </div>
        </section>
      </main>

      <footer className="lux-footer landing-container" id="terms">
        <BrandMark />
        <div><a href="#privacy">Privacy Protocol</a><a href="#terms">Terms of Service</a></div>
        <p>CLOUD PLAYER {APP_VERSION_LABEL} © 2026. DISCREET. SECURE. PRIVATE.</p>
      </footer>
    </div>
  )
}
